import React, { useState, useEffect } from 'react';
import { authService } from '../../services/authService';

const MARCAS = ['Ariston', 'Chaffoteaux', 'Fleck', 'Saunier Duval', 'Vaillant', 'Junkers', 'Otro'];
const TIPOS  = ['Aerotermia', 'Calentador', 'Termo', 'Caldera', 'Bomba de calor', 'Otro'];

const mapearTipoEquipo = (descripcion) => {
  if (!descripcion) return 'Termo';
  const d = descripcion.toLowerCase();
  if (d.includes('nuos') || d.includes('termo'))       return 'Termo';
  if (d.includes('aerotermia') || d.includes('bomba')) return 'Aerotermia';
  if (d.includes('calentador'))                         return 'Calentador';
  if (d.includes('caldera'))                            return 'Caldera';
  return 'Otro';
};

const formatearFecha = (fechaInput) => {
  if (!fechaInput) return null;
  try {
    const fecha = typeof fechaInput === 'string' && fechaInput.match(/^\d{4}-\d{2}-\d{2}$/)
      ? new Date(fechaInput + 'T12:00:00.000Z')
      : new Date(fechaInput);
    if (isNaN(fecha.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${fecha.getUTCFullYear()}-${p(fecha.getUTCMonth()+1)}-${p(fecha.getUTCDate())}T${p(fecha.getUTCHours())}:${p(fecha.getUTCMinutes())}:${p(fecha.getUTCSeconds())}Z`;
  } catch { return null; }
};

const calcularAntiguedad = (fechaInstalacion) => {
  if (!fechaInstalacion) return '';
  const diff = new Date() - new Date(fechaInstalacion);
  const años  = Math.floor(diff / (365.25 * 864e5));
  const meses = Math.floor((diff % (365.25 * 864e5)) / (30.44 * 864e5));
  return años > 0 ? `${años}a ${meses}m` : `${meses} mes${meses !== 1 ? 'es' : ''}`;
};

const Step3ProductData = ({ formData, updateFormData }) => {
  const [modo, setModo]             = useState('asset');
  const [assets, setAssets]         = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [errorAssets, setErrorAssets] = useState(null);
  const [guardando, setGuardando]   = useState(false);
  const [errorGuardar, setErrorGuardar] = useState(null);

  const cargarAssets = async () => {
    if (!formData.clienteId) return;
    setCargando(true);
    setErrorAssets(null);
    try {
      const res  = await fetch(`/.netlify/functions/getAssetsByAccount?accountId=${formData.clienteId}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) {
        setAssets(data.assets);
        if (data.assets.length === 0) setErrorAssets('Sin equipos registrados');
      } else {
        setErrorAssets(data.error || 'Error al cargar equipos');
      }
    } catch (e) {
      setErrorAssets('Error de conexión');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (modo === 'asset' && formData.clienteVerificado) cargarAssets();
  }, [modo]);

  useEffect(() => {
    if (assets.length > 0 && modo !== 'asset') setModo('asset');
  }, [assets.length]);

  const handleModo = (m) => {
    setModo(m);
    if (m === 'manual') {
      updateFormData({ marca: '', tipoEquipo: '', modelo: '', numeroSerie: '', fechaInstalacion: '', instaladoPor: '', assetId: null });
      setErrorGuardar(null);
    }
  };

  const handleAssetSelect = (asset) => {
    updateFormData({
      marca:            asset.brand || 'Sin marca',
      tipoEquipo:       mapearTipoEquipo(asset.description || asset.model || asset.name),
      modelo:           asset.model || asset.name || 'Modelo no especificado',
      numeroSerie:      asset.serialNumber || asset['serial-number'] || '',
      fechaInstalacion: formatearFecha(asset.warrantyStartDate || asset['warranty-start-date']),
      instaladoPor:     'Climatización Zapata',
      assetId:          asset.id,
      assetReference:   asset.reference,
      assetCreatedManually: false,
    });
  };

  const guardarEquipo = async () => {
    if (!formData.marca || !formData.modelo || !formData.tipoEquipo) return;
    if (formData.assetId) return;
    setGuardando(true);
    setErrorGuardar(null);
    try {
      const res  = await fetch('/.netlify/functions/createAsset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteId:        formData.clienteId,
          addressId:        formData.addressId,
          marca:            formData.marca,
          modelo:           formData.modelo,
          tipoEquipo:       formData.tipoEquipo,
          numeroSerie:      formData.numeroSerie || '',
          fechaInstalacion: formatearFecha(formData.fechaInstalacion),
          instaladoPor:     formData.instaladoPor || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        updateFormData({ assetId: data.asset.id, assetReference: data.asset.reference, assetCreatedManually: true });
      } else {
        setErrorGuardar(data.error || 'Error al guardar');
      }
    } catch (e) {
      setErrorGuardar('Error de conexión');
    } finally {
      setGuardando(false);
    }
  };

  const puedeGuardar = !formData.assetId && formData.clienteVerificado && formData.addressId &&
    formData.marca && formData.modelo && formData.tipoEquipo;

  // Estado garantía compacto
  const garantiaPill = () => {
    if (!formData.fechaInstalacion) return null;
    const meses = (new Date() - new Date(formData.fechaInstalacion)) / (30.44 * 864e5);
    if (meses <= 24)  return { label: 'En garantía', cls: 'bg-emerald-100 text-emerald-700' };
    if (meses <= 36)  return { label: 'Garantía posible', cls: 'bg-amber-100 text-amber-700' };
    return { label: 'Garantía expirada', cls: 'bg-red-100 text-red-700' };
  };
  const gpill = formData.tipoIncidencia === 'sat' ? garantiaPill() : null;

  return (
    <div className="px-4 py-3 space-y-3">

      {/* Toggle modo */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        {[
          { id: 'asset',  label: '📋 Registrados' },
          { id: 'manual', label: '✏️ Manual' },
        ].map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => handleModo(id)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              modo === id
                ? 'bg-white shadow text-gray-800'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Modo: activos registrados */}
      {modo === 'asset' && (
        <>
          {cargando && (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
              <div className="w-3.5 h-3.5 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
              Cargando equipos…
            </div>
          )}
          {errorAssets && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
              <span className="flex-1">{errorAssets}</span>
              <button onClick={cargarAssets} className="font-semibold underline flex-shrink-0">Reintentar</button>
            </div>
          )}
          {!cargando && assets.length > 0 && (
            <div className="space-y-1.5">
              {assets.map(asset => {
                const sel = formData.assetId === asset.id;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => handleAssetSelect(asset)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all ${
                      sel ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className="text-base flex-shrink-0">{sel ? '✅' : '📦'}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold leading-tight truncate ${sel ? 'text-emerald-800' : 'text-gray-800'}`}>
                        {asset.brand || 'Sin marca'} · {asset.model || 'Sin modelo'}
                        {asset.hasWarranty && <span className="ml-1">🛡️</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {[asset.serialNumber && `S/N: ${asset.serialNumber}`, asset.warrantyStartDate && `Inst: ${new Date(asset.warrantyStartDate).toLocaleDateString('es-ES')}`].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {sel && gpill && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${gpill.cls}`}>{gpill.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modo: manual */}
      {modo === 'manual' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Marca *</label>
              <select
                value={formData.marca || ''}
                onChange={e => updateFormData({ marca: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-red-400"
              >
                <option value="">Seleccionar</option>
                {MARCAS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo *</label>
              <select
                value={formData.tipoEquipo || ''}
                onChange={e => updateFormData({ tipoEquipo: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-red-400"
              >
                <option value="">Seleccionar</option>
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Modelo *</label>
            <input
              type="text"
              value={formData.modelo || ''}
              onChange={e => updateFormData({ modelo: e.target.value })}
              placeholder="Ej: NUOS 80, CARES PREMIUM 24…"
              className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-red-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">N/S <span className="font-normal text-gray-400">(opcional)</span></label>
              <input
                type="text"
                value={formData.numeroSerie || ''}
                onChange={e => updateFormData({ numeroSerie: e.target.value })}
                placeholder="Número de serie"
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-red-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Instalación <span className="font-normal text-gray-400">(opcional)</span>
              </label>
              <input
                type="date"
                value={formData.fechaInstalacion || ''}
                onChange={e => updateFormData({ fechaInstalacion: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-red-400"
              />
            </div>
          </div>

          {/* Antigüedad + garantía */}
          {formData.fechaInstalacion && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">⏱ {calcularAntiguedad(formData.fechaInstalacion)}</span>
              {gpill && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gpill.cls}`}>{gpill.label}</span>
              )}
            </div>
          )}

          {/* Guardar en el sistema */}
          {errorGuardar && (
            <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{errorGuardar}</p>
          )}
          {formData.assetId && (
            <p className="text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg">✅ Equipo guardado en el sistema</p>
          )}
          {puedeGuardar && (
            <button
              type="button"
              onClick={guardarEquipo}
              disabled={guardando}
              className="w-full text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-xl py-2 transition-colors disabled:opacity-50"
            >
              {guardando ? 'Guardando…' : '💾 Guardar equipo en el sistema (opcional)'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Step3ProductData;
